import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import API from "../config/api.js";

export default function AppBanners() {
  const { subscription } = useAuth();
  const [showFloating, setShowFloating]     = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [confirming, setConfirming]         = useState(false);
  const sentinelRef = useRef(null);

  const hasSubscription = !!subscription;
  const isTrial      = subscription?.status === "trialing" && subscription?.gateway === "stripe";
  const isCanceled   = (subscription?.status === "active" || subscription?.status === "trialing")
                     && !!subscription?.canceledat
                     && subscription?.gateway === "stripe";

  // Expose modal opener globally — always, regardless of which banner is showing
  useEffect(() => {
    window._openSkipTrialModal = () => setShowTrialModal(true);
    return () => { delete window._openSkipTrialModal; };
  }, []);

  // Show floating banner when fixed banner scrolls out of view
  useEffect(() => {
    if (hasSubscription) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFloating(!entry.isIntersecting),
      { threshold: 0 }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasSubscription]);

  const handleSkipTrial = async () => {
    setConfirming(true);
    try {
      await fetch(`${API.stripe}?skipTrial&idgateway=${subscription.idgateway}`, {
        credentials: "include",
      });
      setShowTrialModal(false);
      window.location.reload();
    } catch (err) {
      console.error("skipTrial error:", err);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      {/* ── No subscription — fixed + floating banners ── */}
      {!hasSubscription && (
        <>
          <div
            ref={sentinelRef}
            className="bg-secondary rounded-4 shadow-sm p-4 mb-5"
            id="bannerUnlockSchedule"
          >
            <div className="row align-items-center g-4">
              <div className="col-md-9 fs-5 fw-bold">
                Unlock the full calendar, scheduling, templates and all other features
              </div>
              <div className="col-md-3">
                <a className="btn btn-primary w-100" href="/plans">Subscribe Now</a>
              </div>
            </div>
          </div>

          {showFloating && (
            <div id="bannerLimitedExperience" className="top-0 mt-4 mx-auto animate__animated animate__fadeIn">
              <div className="bg-secondary-light rounded-4 shadow-sm py-3 px-4">
                <div className="row align-items-center g-4">
                  <div className="col-9 fs-6 fw-bold">
                    🔑 Ready to unlock unlimited content access to more templates and platform features?
                  </div>
                  <div className="col-3">
                    <a href="/plans" className="btn btn-primary w-100">Subscribe Now</a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Trialing — upgrade banner ── */}
      {isTrial && (
        <div className="bg-secondary rounded-4 shadow-sm p-4 mb-5">
          <div className="row align-items-center g-4">
            <div className="col-md-9 fs-6 fw-bold">
              🔑 Ready to turn this into a weekly inquiry engine? Upgrade and turn your content into consistent clients
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-primary w-100" onClick={() => setShowTrialModal(true)}>
                Unlock All Features
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Canceled — reactivate banner ── */}
      {isCanceled && (
        <div style={{ position: "sticky", top: 88, zIndex: 10 }} className="bg-secondary rounded-4 shadow-sm p-4 mb-5">
          <div className="row align-items-center g-4">
            <div className="col-md-9 fs-6 fw-bold">
              🔑 Your inquiry engine is paused. Reactivate Social Jet and put your weekly revenue system back in motion.
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-primary w-100" onClick={() => window._openSkipTrialModal?.()}>
                  Reactivate Subscription
                </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Skip trial modal — always in DOM when needed ── */}
      {showTrialModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setShowTrialModal(false)}
          />
          <div className="modal fade show d-block" style={{ zIndex: 1050 }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0 p-4 mx-2">
                  <h5 className="modal-title w-100">Start Your Membership</h5>
                </div>
                <div className="modal-body p-4">
                  <div className="p-2">
                    <p>Unlock unlimited access to all templates, reels, and downloads by starting your membership today.</p>
                  </div>
                </div>
                <div className="modal-footer p-4">
                  <button type="button" className="btn btn-secondary me-3" onClick={() => setShowTrialModal(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSkipTrial} disabled={confirming}>
                    {confirming ? "Processing..." : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}